// Preview popup geometry: header drag, custom corner resize grip, and the
// window-resize fail-safe that keeps the popup fully on-screen.

import { getPreviewState, setPreviewState } from '../state.js';
import {
	savePreviewPosition,
	savePreviewSize,
	clampPreviewSize,
	PREVIEW_VIEWPORT_MARGIN,
	PREVIEW_DEFAULT_W,
	PREVIEW_DEFAULT_H
} from '../position.js';

// Header buttons/handles that must NOT start a drag or auto-pin.
const HEADER_NO_DRAG = '.preview-popup__close, .preview-popup__open, .preview-popup__pin, .preview-popup__copy-link, .preview-popup__tg, .preview-popup__fav, .preview-popup__download, .preview-popup__nav, .preview-popup__resize-handle';

export function setupDragFunctionality(popupElement, newState, setPinned) {
	const header = popupElement.find('.preview-popup__header');
	let isDragging = false;
	let dragStartX = 0;
	let dragStartY = 0;
	let popupStartLeft = 0;
	let popupStartTop = 0;

	// Auto-pin on header click (user action)
	header.off('click.preview-header-pin').on('click.preview-header-pin', function(e) {
		const isTrusted = e.isTrusted !== undefined ? e.isTrusted : (e.originalEvent && e.originalEvent.isTrusted !== undefined ? e.originalEvent.isTrusted : true);
		// Ignore programmatic clicks (isTrusted: false)
		if (isTrusted === false) return;

		// Don't pin if clicking on buttons
		if ($(e.target).closest(HEADER_NO_DRAG).length) {
			return;
		}

		const currentState = getPreviewState();
		const setPinnedFn = currentState.setPinnedCallback || setPinned;
		if (typeof setPinnedFn === 'function' && !currentState.previewPopupPinned) {
			setPinnedFn(true);
		}
	});

	// Auto-pin on header mousedown (user action) - before drag starts
	header.off('mousedown.preview-header-drag').on('mousedown.preview-header-drag', function(e) {
		const isTrusted = e.isTrusted !== undefined ? e.isTrusted : (e.originalEvent && e.originalEvent.isTrusted !== undefined ? e.originalEvent.isTrusted : true);
		// Don't start drag if clicking on buttons
		if ($(e.target).closest(HEADER_NO_DRAG).length) {
			return;
		}

		// Auto-pin on header mousedown (user action) - before drag starts
		if (isTrusted !== false) {
			const currentState = getPreviewState();
			const setPinnedFn = currentState.setPinnedCallback || setPinned;
			if (typeof setPinnedFn === 'function' && !currentState.previewPopupPinned) {
				setPinnedFn(true);
			}
		}

		isDragging = true;
		const popupRect = popupElement[0].getBoundingClientRect();
		dragStartX = e.clientX;
		dragStartY = e.clientY;
		popupStartLeft = popupRect.left;
		popupStartTop = popupRect.top;
		e.preventDefault();
		popupElement.addClass('preview-popup--dragging');
	});

	$(document).on('mousemove.preview-drag', function(e) {
		if (!isDragging || !popupElement || !popupElement.length) return;
		const deltaX = e.clientX - dragStartX;
		const deltaY = e.clientY - dragStartY;
		const newLeft = popupStartLeft + deltaX;
		const newTop = popupStartTop + deltaY;
		const popupWidth = popupElement.outerWidth();
		const popupHeight = popupElement.outerHeight();
		const maxLeft = window.innerWidth - popupWidth;
		const maxTop = window.innerHeight - popupHeight;
		const constrainedLeft = Math.max(0, Math.min(newLeft, maxLeft));
		const constrainedTop = Math.max(0, Math.min(newTop, maxTop));
		popupElement.css({
			left: constrainedLeft + 'px',
			top: constrainedTop + 'px'
		});
		savePreviewPosition(constrainedLeft, constrainedTop);
	});

	$(document).on('mouseup.preview-drag', function() {
		if (isDragging) {
			isDragging = false;
			popupElement.removeClass('preview-popup--dragging');
		}
	});
}

// Custom bottom-right resize grip. Dragging grows/shrinks the popup within
// fail-safe bounds (MIN .. viewport). The iframe's pointer-events are disabled
// during the drag so it does not swallow mousemove; size is persisted on release.
export function setupResizeFunctionality(popupElement) {
	const handle = popupElement.find('.preview-popup__resize-handle');
	if (!handle.length) return;

	let isResizing = false;
	let startX = 0, startY = 0, startW = 0, startH = 0;
	const iframe = () => popupElement.find('.preview-popup__iframe');

	// Double-click the grip = reset to the default size (clamped to the viewport),
	// re-centered into view, and clear the saved size.
	handle.on('dblclick.preview-resize', function(e) {
		e.preventDefault();
		e.stopPropagation();
		const rect = popupElement[0].getBoundingClientRect();
		const size = clampPreviewSize(PREVIEW_DEFAULT_W, PREVIEW_DEFAULT_H, PREVIEW_VIEWPORT_MARGIN, PREVIEW_VIEWPORT_MARGIN);
		let left = Math.min(rect.left, window.innerWidth - size.width - PREVIEW_VIEWPORT_MARGIN);
		let top = Math.min(rect.top, window.innerHeight - size.height - PREVIEW_VIEWPORT_MARGIN);
		left = Math.max(PREVIEW_VIEWPORT_MARGIN, left);
		top = Math.max(PREVIEW_VIEWPORT_MARGIN, top);
		popupElement.css({ width: size.width + 'px', height: size.height + 'px', left: left + 'px', top: top + 'px' });
		savePreviewSize(size.width, size.height);
		savePreviewPosition(left, top);
	});

	handle.on('mousedown.preview-resize', function(e) {
		e.preventDefault();
		e.stopPropagation();
		isResizing = true;
		const rect = popupElement[0].getBoundingClientRect();
		startX = e.clientX;
		startY = e.clientY;
		startW = rect.width;
		startH = rect.height;
		popupElement.addClass('preview-popup--resizing');
		iframe().css('pointer-events', 'none');
		// Pin so the popup does not auto-close mid-resize
		const currentState = getPreviewState();
		const setPinnedFn = currentState.setPinnedCallback;
		if (typeof setPinnedFn === 'function' && !currentState.previewPopupPinned) {
			setPinnedFn(true);
		}
	});

	$(document).on('mousemove.preview-resize', function(e) {
		if (!isResizing || !popupElement || !popupElement.length) return;
		const rect = popupElement[0].getBoundingClientRect();
		const desiredW = startW + (e.clientX - startX);
		const desiredH = startH + (e.clientY - startY);
		const clamped = clampPreviewSize(desiredW, desiredH, rect.left, rect.top);
		popupElement.css({ width: clamped.width + 'px', height: clamped.height + 'px' });
	});

	$(document).on('mouseup.preview-resize', function() {
		if (!isResizing) return;
		isResizing = false;
		popupElement.removeClass('preview-popup--resizing');
		iframe().css('pointer-events', '');
		if (popupElement && popupElement.length) {
			const rect = popupElement[0].getBoundingClientRect();
			savePreviewSize(rect.width, rect.height);
		}
	});
}

// Debounced window-resize handler: on viewport shrink, clamp the popup's size to
// the new viewport and nudge it back inside so it never lands off-screen.
export function setupWindowResizeFailsafe(popupElement, newState) {
	let debounce = null;
	const handler = function() {
		clearTimeout(debounce);
		debounce = setTimeout(function() {
			const el = popupElement && popupElement.length ? popupElement[0] : null;
			if (!el || !document.body.contains(el)) return;
			const rect = el.getBoundingClientRect();
			const size = clampPreviewSize(rect.width, rect.height, PREVIEW_VIEWPORT_MARGIN, PREVIEW_VIEWPORT_MARGIN);
			let left = Math.min(rect.left, window.innerWidth - size.width - PREVIEW_VIEWPORT_MARGIN);
			let top = Math.min(rect.top, window.innerHeight - size.height - PREVIEW_VIEWPORT_MARGIN);
			left = Math.max(PREVIEW_VIEWPORT_MARGIN, left);
			top = Math.max(PREVIEW_VIEWPORT_MARGIN, top);
			popupElement.css({
				width: size.width + 'px',
				height: size.height + 'px',
				left: left + 'px',
				top: top + 'px'
			});
			savePreviewPosition(left, top);
		}, 120);
	};
	window.addEventListener('resize', handler);
	newState.windowResizeHandler = handler;
	setPreviewState(newState);
}
